import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { documentAPI } from "../services/api";
import { toast } from "react-toastify";
import {
  Shield,
  CheckCircle,
  XCircle,
  FileText,
  Calendar,
  User,
  Download,
  Copy,
  AlertCircle,
  ExternalLink,
} from "lucide-react";
import { safeFormatDateTime } from "../utils/dateUtils";

function PublicVerify() {
  const { documentId } = useParams();
  const [docData, setDocData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [verificationResult, setVerificationResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const verifySignature = async (doc) => {
      if (!doc) return;

      setVerifying(true);
      try {
        console.log("Starting verification for document:", documentId);
        const response = await documentAPI.verifyDocumentPublic(documentId);
        console.log("Verification response:", response);
        console.log("Verification response data:", response.data);
        setVerificationResult(response.data);
        if (response.data.signatureValid) {
          toast.success("✅ Signature verification successful!");
        } else {
          toast.error("❌ Signature verification failed");
        }
      } catch (error) {
        console.error("Verification error:", error);
        setVerificationResult({
          signatureValid: false,
          error: "Verification failed",
          message: error.response?.data?.error || "Unable to verify signature",
        });
        toast.error("Failed to verify signature");
      } finally {
        setVerifying(false);
      }
    };

    const fetchDocument = async () => {
      try {
        const response = await documentAPI.getPublicDocument(documentId, true);
        setDocData(response.data);
        // Auto-verify if document is loaded
        await verifySignature(response.data);
      } catch (error) {
        console.error("Document fetch error:", error);
        setError(
          "Document not found or not accessible for public verification"
        );
      } finally {
        setLoading(false);
      }
    };

    if (documentId) {
      fetchDocument();
    } else {
      setLoading(false);
      setError("No document ID provided");
    }
  }, [documentId]);

  const verifySignature = async () => {
    if (!docData) return;

    setVerifying(true);
    try {
      console.log("Starting verification for document:", documentId);
      const response = await documentAPI.verifyDocumentPublic(documentId);
      console.log("Verification response:", response);
      console.log("Verification response data:", response.data);
      setVerificationResult(response.data);
      if (response.data.signatureValid) {
        toast.success("✅ Signature verification successful!");
      } else {
        toast.error("❌ Signature verification failed");
      }
    } catch (error) {
      console.error("Verification error:", error);
      setVerificationResult({
        signatureValid: false,
        error: "Verification failed",
        message: error.response?.data?.error || "Unable to verify signature",
      });
      toast.error("Failed to verify signature");
    } finally {
      setVerifying(false);
    }
  };

  const copyDocumentLink = () => {
    const url = window.location.href;

    // Try modern clipboard API first, fallback to older methods
    const copyToClipboard = async (text) => {
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(text);
          return true;
        } else {
          // Fallback for older browsers or non-secure contexts
          const textArea = document.createElement("textarea");
          textArea.value = text;
          textArea.style.position = "absolute";
          textArea.style.left = "-999999px";
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();
          try {
            const successful = document.execCommand("copy");
            document.body.removeChild(textArea);
            return successful;
          } catch (err) {
            document.body.removeChild(textArea);
            return false;
          }
        }
      } catch (err) {
        return false;
      }
    };

    copyToClipboard(url).then((success) => {
      if (success) {
        toast.success("Verification link copied to clipboard");
      } else {
        const message = `Copy this verification link:\n\n${url}`;
        if (window.confirm(message + "\n\nClick OK to close this dialog.")) {
          // User clicked OK
        }
        toast.info("Please copy the verification link manually");
      }
    });
  };

  const copySignature = () => {
    if (docData?.signature) {
      const copyToClipboard = async (text) => {
        try {
          if (navigator.clipboard && window.isSecureContext) {
            await navigator.clipboard.writeText(text);
            return true;
          } else {
            const textArea = document.createElement("textarea");
            textArea.value = text;
            textArea.style.position = "absolute";
            textArea.style.left = "-999999px";
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            try {
              const successful = document.execCommand("copy");
              document.body.removeChild(textArea);
              return successful;
            } catch (err) {
              document.body.removeChild(textArea);
              return false;
            }
          }
        } catch (err) {
          return false;
        }
      };

      copyToClipboard(docData.signature).then((success) => {
        if (success) {
          toast.success("Signature copied to clipboard");
        } else {
          const message = `Copy this signature:\n\n${docData.signature}`;
          if (window.confirm(message + "\n\nClick OK to close this dialog.")) {
            // User clicked OK
          }
          toast.info("Please copy the signature manually");
        }
      });
    }
  };

  const downloadDocument = () => {
    if (docData?.documentData) {
      const link = document.createElement("a");
      link.href = docData.documentData;
      link.download = `${docData.documentName || "signed-document"}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("Signed document downloaded");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading document for verification...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md mx-auto text-center">
          <AlertCircle className="h-16 w-16 mx-auto mb-4 text-red-500" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Document Not Found
          </h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <div className="bg-gray-100 p-4 rounded-lg">
            <p className="text-sm text-gray-700 mb-2">
              To verify a document, you need a valid document verification link.
            </p>
            <p className="text-sm text-gray-500">
              Document verification links look like:
              <br />
              <code className="text-xs">yoursite.com/verify/[document-id]</code>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Shield className="h-8 w-8 text-blue-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Document Verification
                </h1>
                <p className="text-gray-600">
                  Public verification of digital signature
                </p>
              </div>
            </div>
            <button
              onClick={copyDocumentLink}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-md text-sm"
            >
              <ExternalLink className="h-4 w-4" />
              <span>Share Link</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Verification Status */}
        <div className="mb-8">
          <div
            className={`p-6 rounded-lg border-2 ${
              verificationResult === null
                ? "border-gray-300 bg-gray-50"
                : verificationResult.signatureValid
                ? "border-green-300 bg-green-50"
                : "border-red-300 bg-red-50"
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                {verifying ? (
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                ) : verificationResult === null ? (
                  <Shield className="h-8 w-8 text-gray-400" />
                ) : verificationResult.signatureValid ? (
                  <CheckCircle className="h-8 w-8 text-green-600" />
                ) : (
                  <XCircle className="h-8 w-8 text-red-600" />
                )}
                <div>
                  <h3 className="text-lg font-semibold">
                    {verifying
                      ? "Verifying signature..."
                      : verificationResult === null
                      ? "Ready to verify"
                      : verificationResult.signatureValid
                      ? "✅ Signature Valid"
                      : "❌ Signature Invalid"}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {verifying
                      ? "Please wait while we verify the digital signature"
                      : verificationResult === null
                      ? "Document loaded, verification in progress"
                      : verificationResult.signatureValid
                      ? "This document has a valid digital signature"
                      : verificationResult.message ||
                        "The signature could not be verified"}
                  </p>
                </div>
              </div>
              {!verifying && verificationResult && (
                <button
                  onClick={verifySignature}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm"
                >
                  Verify Again
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Document Details */}
        {docData && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Document Information */}
            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <FileText className="h-5 w-5 mr-2" />
                  Document Information
                </h3>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Document Name
                    </label>
                    <p className="text-gray-900">{docData.documentName}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Document Type
                    </label>
                    <p className="text-gray-900">
                      {docData.documentType || "PDF"}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      File Size
                    </label>
                    <p className="text-gray-900">
                      {docData.documentSize
                        ? `${(docData.documentSize / 1024).toFixed(1)} KB`
                        : "Unknown"}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Document Hash
                    </label>
                    <p className="text-xs text-gray-900 font-mono break-all bg-gray-50 p-2 rounded">
                      {docData.documentHash}
                    </p>
                  </div>
                </div>
              </div>

              {/* Signature Details */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Shield className="h-5 w-5 mr-2" />
                  Signature Details
                </h3>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Signed By
                    </label>
                    <p className="text-gray-900 flex items-center">
                      <User className="h-4 w-4 mr-1" />
                      {docData.signedBy}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Signed At
                    </label>
                    <p className="text-gray-900 flex items-center">
                      <Calendar className="h-4 w-4 mr-1" />
                      {safeFormatDateTime(docData.signedAt)}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">
                      Algorithm
                    </label>
                    <p className="text-gray-900">
                      {docData.signatureAlgorithm}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500 flex items-center justify-between">
                      Digital Signature
                      <button
                        onClick={copySignature}
                        className="text-blue-600 hover:text-blue-800"
                        title="Copy signature"
                      >
                        <Copy className="h-4 w-4" />
                      </button>
                    </label>
                    <p className="text-xs text-gray-900 font-mono break-all bg-gray-50 p-2 rounded max-h-32 overflow-y-auto">
                      {docData.signature}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions and Verification Results */}
            <div className="space-y-6">
              {/* Download Section */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Download Signed Document
                </h3>
                <p className="text-gray-600 mb-4">
                  Download the original signed document. This is the exact
                  document that was digitally signed.
                </p>
                <button
                  onClick={downloadDocument}
                  disabled={!docData?.documentData}
                  className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-md"
                >
                  <Download className="h-4 w-4" />
                  <span>Download Signed Document</span>
                </button>
              </div>

              {/* Verification Details */}
              {verificationResult && (
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Verification Results
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-500">
                        Signature Valid:
                      </span>
                      <span
                        className={`font-semibold ${
                          verificationResult.signatureValid
                            ? "text-green-600"
                            : "text-red-600"
                        }`}
                      >
                        {verificationResult.signatureValid ? "✅ Yes" : "❌ No"}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-500">
                        Document Integrity:
                      </span>
                      <span
                        className={`font-semibold ${
                          verificationResult.documentIntegrityValid !== false
                            ? "text-green-600"
                            : "text-red-600"
                        }`}
                      >
                        {verificationResult.documentIntegrityValid !== false
                          ? "✅ Intact"
                          : "❌ Modified"}
                      </span>
                    </div>
                    {verificationResult.verificationDate && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-gray-500">
                          Verified At:
                        </span>
                        <span className="text-sm text-gray-900">
                          {safeFormatDateTime(
                            verificationResult.verificationDate
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Help Section */}
              <div className="bg-blue-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-blue-900 mb-2">
                  About Digital Signatures
                </h3>
                <div className="text-sm text-blue-800 space-y-2">
                  <p>
                    ✅ <strong>Valid signature</strong> means the document is
                    authentic and hasn't been tampered with since signing.
                  </p>
                  <p>
                    🔒 <strong>Document integrity</strong> ensures the content
                    matches exactly what was signed.
                  </p>
                  <p>
                    📋 <strong>Public verification</strong> allows anyone to
                    verify authenticity without needing special software.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default PublicVerify;
